import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const COLORS = ['#f5c400','#c9a000','#10b981','#7c3aed','#444444','#0088cc','#ef4444','#f09433','#bc1888','#25D366'];
const empty = { label: '', type: 'credits', value: 0, weight: 10, color: '#f5c400', isActive: true, order: 0 };

export default function AdminWheelSpins() {
  const [tab, setTab] = useState('prizes');
  const [prizes, setPrizes] = useState([]);
  const [spins, setSpins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const loadPrizes = () => api.get('/wheel/admin/prizes').then(({ data }) => setPrizes(data.prizes || []));
  const loadSpins  = () => api.get('/wheel/admin/spins').then(({ data }) => setSpins(data.spins || []));

  useEffect(() => {
    Promise.all([loadPrizes(), loadSpins()]).finally(() => setLoading(false));
  }, []);

  const openAdd  = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (p) => { setEditing(p._id); setForm({ label: p.label, type: p.type, value: p.value, weight: p.weight, color: p.color, isActive: p.isActive, order: p.order || 0 }); setModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) await api.put(`/wheel/admin/prizes/${editing}`, form);
      else         await api.post('/wheel/admin/prizes', form);
      setModal(false); loadPrizes();
    } catch (err) { alert(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prize?')) return;
    try { await api.delete(`/wheel/admin/prizes/${id}`); loadPrizes(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const filtered = spins.filter(s => s.user?.username?.toLowerCase().includes(search.toLowerCase()));
  const totalCredits  = spins.filter(s => s.prizeType==='credits').reduce((a,b) => a+(b.prizeValue||0), 0);
  const totalFreeplay = spins.filter(s => s.prizeType==='freeplay').reduce((a,b) => a+(b.prizeValue||0), 0);
  const totalWeight   = prizes.reduce((a,p) => a + (p.isActive ? p.weight : 0), 0);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:24 }}>WHEEL OF FORTUNE</h1>
          <p style={{ color:'var(--text-secondary)', fontSize:13, marginTop:4 }}>Manage prizes and view spin history</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>+ Add Prize</button>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:24 }}>
        {[
          { label:'Active Prizes',    val: prizes.filter(p=>p.isActive).length, color:'#f5c400' },
          { label:'Total Spins',      val: spins.length,                        color:'#c9a000' },
          { label:'Credits Awarded',  val: `$${totalCredits.toLocaleString()}`, color:'#10b981' },
          { label:'Freeplay Awarded', val: `$${totalFreeplay}`,                 color:'#7c3aed' },
        ].map(s => (
          <div key={s.label} className="card" style={{ borderLeft:`3px solid ${s.color}` }}>
            <div style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:6 }}>{s.label}</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:900, color:s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {[['prizes','🏆 Prizes'],['spins','📋 Spin History']].map(([key,label]) => (
          <button key={key} onClick={() => setTab(key)} className={`btn btn-sm ${tab===key?'btn-primary':'btn-secondary'}`}>{label}</button>
        ))}
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner"/></div> : (
        <>
          {tab === 'prizes' && (
            <div>
              <div style={{ background:'rgba(245,196,0,0.06)', border:'1px solid rgba(245,196,0,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:13, color:'var(--text-secondary)' }}>
                💡 <strong style={{ color:'var(--text-primary)' }}>Weight = Probability.</strong> Higher weight = more likely to land. Total active weight: <strong style={{ color:'#f5c400' }}>{totalWeight}</strong>. Each prize probability = its weight ÷ {totalWeight}.
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:16 }}>
                {prizes.map(p => {
                  const pct = totalWeight > 0 ? ((p.weight / totalWeight) * 100).toFixed(1) : 0;
                  return (
                    <div key={p._id} className="card" style={{ borderLeft:`4px solid ${p.color}`, opacity: p.isActive ? 1 : 0.5 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                        <div style={{ width:16, height:16, borderRadius:'50%', background:p.color, flexShrink:0 }} />
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:15 }}>{p.label}</div>
                          <div style={{ fontSize:12, color:'var(--text-muted)', textTransform:'capitalize' }}>{p.type}{p.value > 0 ? ` — $${p.value}` : ''}</div>
                        </div>
                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background: p.isActive?'rgba(16,185,129,0.12)':'rgba(239,68,68,0.12)', color: p.isActive?'#10b981':'#ef4444', fontWeight:700 }}>
                          {p.isActive ? 'On' : 'Off'}
                        </span>
                      </div>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10, fontSize:13 }}>
                        <span style={{ color:'var(--text-secondary)' }}>Weight: <strong style={{ color:'var(--text-primary)' }}>{p.weight}</strong></span>
                        <span style={{ color:'#f5c400', fontWeight:700 }}>~{pct}% chance</span>
                      </div>
                      {/* Mini probability bar */}
                      <div style={{ height:5, background:'var(--border)', borderRadius:3, marginBottom:12, overflow:'hidden' }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:p.color, borderRadius:3, transition:'width 0.3s' }} />
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        <button className="btn btn-secondary btn-sm" style={{ flex:1 }} onClick={() => openEdit(p)}>✏ Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p._id)}>🗑</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'spins' && (
            <>
              <input placeholder="Search by username..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth:300, marginBottom:20 }} />
              <div className="card" style={{ padding:0, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid var(--border)', background:'var(--bg-secondary)' }}>
                      {['User','Date','Prize','Type','Value'].map(h => (
                        <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:11, color:'var(--text-secondary)', fontFamily:'var(--font-display)', letterSpacing:'0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(s => (
                      <tr key={s._id} style={{ borderBottom:'1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background='var(--bg-card-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <td style={{ padding:'12px 16px' }}>
                          <div style={{ fontWeight:700, fontSize:14 }}>{s.user?.username}</div>
                          <div style={{ fontSize:11, color:'var(--text-muted)' }}>{s.user?.email}</div>
                        </td>
                        <td style={{ padding:'12px 16px', fontSize:13, color:'var(--text-secondary)' }}>{s.date}</td>
                        <td style={{ padding:'12px 16px', fontWeight:600, fontSize:14 }}>{s.prize}</td>
                        <td style={{ padding:'12px 16px' }}>
                          <span style={{ padding:'3px 10px', borderRadius:12, fontSize:12, fontWeight:700,
                            background: s.prizeType==='freeplay'?'rgba(240,148,51,0.12)':s.prizeType==='nothing'?'rgba(100,100,100,0.12)':'rgba(245,196,0,0.12)',
                            color: s.prizeType==='freeplay'?'#f09433':s.prizeType==='nothing'?'#666':'#f5c400', textTransform:'capitalize' }}>
                            {s.prizeType}
                          </span>
                        </td>
                        <td style={{ padding:'12px 16px', fontFamily:'var(--font-display)', fontWeight:700, color:s.prizeValue>0?'#10b981':'#555' }}>
                          {s.prizeValue > 0 ? `$${s.prizeValue}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && <div style={{ padding:'40px', textAlign:'center', color:'var(--text-secondary)' }}>No spins found</div>}
              </div>
            </>
          )}
        </>
      )}

      {/* Prize Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" style={{ maxWidth:500 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setModal(false)}>✕</button>
            <h2 style={{ fontFamily:'var(--font-display)', fontSize:20, marginBottom:24 }}>{editing ? 'Edit Prize' : 'Add Prize'}</h2>
            <form onSubmit={handleSave}>
              <div className="form-group"><label>Prize Label *</label><input value={form.label} onChange={e => setForm({...form, label:e.target.value})} placeholder="e.g. $100 Fun Credits" required /></div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <div className="form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm({...form, type:e.target.value})}>
                    <option value="credits">Fun Credits</option>
                    <option value="freeplay">Freeplay</option>
                    <option value="nothing">Try Again (nothing)</option>
                  </select>
                </div>
                <div className="form-group"><label>Value ($)</label><input type="number" min="0" value={form.value} onChange={e => setForm({...form, value:+e.target.value})} placeholder="0 for Try Again" /></div>
                <div className="form-group">
                  <label>Weight (1–100) <span style={{ color:'var(--text-muted)', fontWeight:400 }}>higher = more likely</span></label>
                  <input type="number" min="1" max="100" value={form.weight} onChange={e => setForm({...form, weight:+e.target.value})} />
                </div>
                <div className="form-group"><label>Display Order</label><input type="number" value={form.order} onChange={e => setForm({...form, order:+e.target.value})} /></div>
              </div>
              {/* Color picker */}
              <div className="form-group">
                <label>Segment Color</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                  {COLORS.map(c => (
                    <div key={c} onClick={() => setForm({...form, color:c})} style={{ width:28, height:28, borderRadius:'50%', background:c, cursor:'pointer', border:`3px solid ${form.color===c?'#fff':'transparent'}`, transition:'border 0.15s' }} />
                  ))}
                  <input type="color" value={form.color} onChange={e => setForm({...form, color:e.target.value})} style={{ width:28, height:28, padding:0, borderRadius:'50%', border:'none', cursor:'pointer' }} title="Custom color" />
                </div>
              </div>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', color:'var(--text-secondary)', marginBottom:20 }}>
                <input type="checkbox" checked={form.isActive} onChange={e => setForm({...form, isActive:e.target.checked})} style={{ width:'auto' }} /> Active (shows on wheel)
              </label>
              <div style={{ display:'flex', gap:12 }}>
                <button type="submit" className="btn btn-primary" style={{ flex:1 }} disabled={saving}>{saving?'Saving...':editing?'Update Prize':'Add Prize'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)} style={{ flex:1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
